// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { openCgrMonitorAssignments } from "./cgr-twin-dialog.js";
import {
  AIRCRAFT_ENGINE_MAKES,
  AIRCRAFT_ENGINE_OTHER_CHOICE,
  aircraftEngineCatalogEntryForMakeModel,
  aircraftEngineMakeOptionsHtml,
  aircraftEngineModelOptionsHtml,
  oilLimitDefaultsForMake,
} from "../core/engine-catalog.js";
import { normalizeFuelPressureConfiguration } from "../core/fuel-pressure-config.js";

export const AIRCRAFT_PROPELLER_MAKES = Object.freeze([
  "McCauley",
  "Hartzell",
  "Sensenich",
  "Catto",
  "MT",
  "Sterba",
  "Tennessee",
  "Whirlwind",
  "Warp Drive",
  "Other",
]);

const AIRCRAFT_PROPELLER_OTHER_CHOICE = "Other";

// Aircraft edit form: field readers, fuel/engine/cylinder/propeller field
// renderers, form-to-profile conversion, and validation. Extracted from
// app.js.
//
// The form is a single <form> in the aircraft edit dialog. These helpers
// read and write its DOM directly — `aircraftEditorForm` finds the form
// (the active one, then the default), `aircraftEditorField` reads a named
// element, and the renderers (`renderAircraftEngineLayoutFields`,
// `renderAircraftPropellerFields`, `resetAircraftForm`) re-populate
// dynamic fields.
//
// Exposed as a factory so the binding stays alive against the hoisted
// helpers and the mutable activeAircraftEditorFormId / aircraftProfiles /
// aircraftFormPhotoDataUrl globals.

export function createAircraftForm({
  // state getters
  getActiveAircraftEditorFormId,
  getAircraftProfiles,
  // constant getters (getters so the binding doesn't fail at factory-call
  // time when the surrounding scope hasn't bound the constant yet)
  getEngineLabelModes,
  getAircraftFuelChoices,
  getAircraftFuelOtherChoice,
  getAircraftPropellerTypes,
  // helpers
  escapeHtml,
  normalizedAircraftType,
  limitedAircraftModel,
  propellerEngineLabel,
  selectedMonitorFromSelects,
  supportedMonitorLabel,
  aircraftDisplayLabel,
  monitorSelectionFromText,
  populateMonitorSelects,
  setAircraftFormPhotoDataUrl,
  syncReadOnlyUi,
  appReadOnlyMode,
  readOnlyPreviewMessage,
  readOnlyActionMessage,
  aircraftProfileEditorValues,
  aircraftFlights,
  getAircraftFormPhotoDataUrl,
}) {
  const fuelPressureDrafts = new WeakMap();

  function aircraftEditorForm(form = null) {
    return form ?? document.querySelector(`#${getActiveAircraftEditorFormId()}`) ?? document.querySelector("#aircraftEditDialogForm");
  }

  function aircraftEditorField(form, name) {
    return aircraftEditorForm(form)?.elements?.[name] ?? null;
  }

  function setAircraftFormStatus(message, statusType = "info", form = null) {
    const editorForm = aircraftEditorForm(form);
    const status = editorForm?.querySelector(".report-status");
    if (!status) return;
    status.textContent = message;
    status.classList.toggle("is-active", Boolean(message));
    status.classList.toggle("is-error", statusType === "error");
    status.classList.toggle("is-success", statusType === "success");
    const autosaveStatus = editorForm.querySelector(".aircraft-autosave-status");
    if (autosaveStatus) {
      autosaveStatus.textContent = statusType === "success" ? "Saved"
        : statusType === "error" ? `Not saved: ${message}`
          : message?.startsWith("Saving ") ? "Saving..." : message || "Changes save automatically";
      autosaveStatus.classList.toggle("is-error", statusType === "error");
      autosaveStatus.classList.toggle("is-success", statusType === "success");
    }
  }

  function aircraftEditSummaryText(values = []) {
    const parts = values.map((value) => String(value ?? "").trim()).filter(Boolean);
    // For a make/model (or engine make/model) pair, don't repeat the make when
    // the model already leads with it: "Piper" + "Piper PA-12" -> "Piper PA-12".
    if (parts.length === 2) {
      const [first, second] = parts;
      const lowerFirst = first.toLowerCase();
      const lowerSecond = second.toLowerCase();
      if (lowerSecond === lowerFirst || lowerSecond.startsWith(`${lowerFirst} `)) return second;
    }
    return parts.join(" ") || null;
  }

  function propellerMakeKey(value) {
    return String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
  }

  function normalizedPropellerMake(value) {
    const text = String(value ?? "").trim();
    if (!text) return "";
    const key = propellerMakeKey(text);
    const exact = AIRCRAFT_PROPELLER_MAKES.find((make) => propellerMakeKey(make) === key);
    if (exact) return exact;
    const inferred = AIRCRAFT_PROPELLER_MAKES.find((make) => {
      const makeKey = propellerMakeKey(make);
      return make !== AIRCRAFT_PROPELLER_OTHER_CHOICE && key.startsWith(makeKey);
    });
    return inferred ?? AIRCRAFT_PROPELLER_OTHER_CHOICE;
  }

  function propellerMakeOptionsHtml(value) {
    const selected = normalizedPropellerMake(value);
    return [
      `<option value="">Select manufacturer...</option>`,
      ...AIRCRAFT_PROPELLER_MAKES.map((make) => {
        const selectedAttr = make === selected ? " selected" : "";
        return `<option value="${escapeHtml(make)}"${selectedAttr}>${escapeHtml(make)}</option>`;
      }),
    ].join("");
  }

  function propellerEntryMake(entry = {}) {
    if (typeof entry === "string") return normalizedPropellerMake(entry);
    if (!entry || typeof entry !== "object") return "";
    const explicitMake = entry.make ?? entry.propMake ?? entry.manufacturer ?? entry.propellerMake;
    return normalizedPropellerMake(explicitMake ?? entry.value ?? entry.model ?? entry.propeller ?? entry.propModel);
  }

  function propellerEntryModel(entry = {}) {
    if (typeof entry === "string") return entry;
    if (!entry || typeof entry !== "object") return "";
    const explicitModel = entry.model ?? entry.propModel;
    const text = String(explicitModel ?? entry.value ?? entry.propeller ?? "").trim();
    if (!text) return "";
    return AIRCRAFT_PROPELLER_MAKES.some((make) => propellerMakeKey(make) === propellerMakeKey(text)) ? "" : text;
  }

  function updateAircraftEditSummary(form = null) {
    form = aircraftEditorForm(form);
    if (!form) return;
    const summaryValue = (key, value) => {
      const element = form.querySelector(`[data-aircraft-summary="${key}"]`);
      if (element) element.textContent = value;
    };
    const tailNumber = String(aircraftEditorField(form, "tailNumber")?.value ?? "").trim().toUpperCase();
    const makeModel = aircraftEditSummaryText([
      aircraftEditorField(form, "make")?.value,
      aircraftEditorField(form, "model")?.value,
    ]);
    const engine = aircraftEditSummaryText([
      aircraftEngineMakeFromForm(form),
      aircraftEngineModelFromForm(form),
    ]);
    const count = formEngineCount(form);
    const cylinderValues = aircraftCylinderEntriesFromForm(form)
      .map((value) => Number.parseInt(String(value ?? "").trim(), 10))
      .filter((value) => Number.isInteger(value) && value > 0);
    const cylinderText = cylinderValues.length === 1
      ? `${cylinderValues[0]} cyl`
      : cylinderValues.length > 1
        ? `${cylinderValues.join("/")} cyl`
        : "";
    summaryValue("tail", tailNumber || "New aircraft");
    summaryValue("aircraft", makeModel || "Make and model");
    summaryValue("engine", engine || "Engine details");
    summaryValue("layout", [count, `engine${count === 1 ? "" : "s"}`, cylinderText].filter(Boolean).join(" "));
  }

  function isKnownAircraftEngineMake(value) {
    const normalized = String(value ?? "").trim();
    return AIRCRAFT_ENGINE_MAKES.includes(normalized) && normalized !== AIRCRAFT_ENGINE_OTHER_CHOICE;
  }

  function canonicalAircraftEngineMake(value) {
    const text = String(value ?? "").trim();
    if (/lycoming/i.test(text)) return "Lycoming";
    if (/continental/i.test(text)) return "Continental";
    if (/titan/i.test(text)) return "Titan";
    if (/cub\s*crafters?/i.test(text)) return "CubCrafters";
    if (/ul\s*power/i.test(text)) return "ULPower";
    if (/rotax/i.test(text)) return "Rotax";
    return isKnownAircraftEngineMake(text) ? text : "";
  }

  function aircraftEngineMakeFromForm(form = null) {
    form = aircraftEditorForm(form);
    const make = String(aircraftEditorField(form, "engineMake")?.value ?? "").trim();
    if (make === AIRCRAFT_ENGINE_OTHER_CHOICE) return String(aircraftEditorField(form, "engineMakeOther")?.value ?? "").trim();
    return make;
  }

  function aircraftEngineModelFromForm(form = null) {
    form = aircraftEditorForm(form);
    const make = String(aircraftEditorField(form, "engineMake")?.value ?? "").trim();
    const model = String(aircraftEditorField(form, "engineModel")?.value ?? "").trim();
    if (make === AIRCRAFT_ENGINE_OTHER_CHOICE || model === AIRCRAFT_ENGINE_OTHER_CHOICE) {
      return String(aircraftEditorField(form, "engineModelOther")?.value ?? "").trim();
    }
    return model;
  }

  function syncAircraftEngineOtherFields(form = null) {
    form = aircraftEditorForm(form);
    if (!form) return;
    const make = String(aircraftEditorField(form, "engineMake")?.value ?? "").trim();
    const model = String(aircraftEditorField(form, "engineModel")?.value ?? "").trim();
    const isOtherMake = make === AIRCRAFT_ENGINE_OTHER_CHOICE;
    const isOtherModel = isOtherMake || model === AIRCRAFT_ENGINE_OTHER_CHOICE;
    const makeOtherField = aircraftEditorField(form, "engineMakeOther");
    const modelOtherField = aircraftEditorField(form, "engineModelOther");
    const modelField = aircraftEditorField(form, "engineModel");
    form.querySelector(".aircraft-engine-make-other-field")?.toggleAttribute("hidden", !isOtherMake);
    form.querySelector(".aircraft-engine-model-other-field")?.toggleAttribute("hidden", !isOtherModel);
    form.querySelector(".aircraft-engine-model-field")?.toggleAttribute("hidden", isOtherMake);
    if (modelField) modelField.disabled = isOtherMake;
    if (makeOtherField) makeOtherField.disabled = !isOtherMake;
    if (modelOtherField) modelOtherField.disabled = !isOtherModel;
  }

  function populateAircraftEngineMakeSelect(form = null, selectedMake = "") {
    form = aircraftEditorForm(form);
    const makeField = aircraftEditorField(form, "engineMake");
    if (!makeField) return;
    makeField.innerHTML = aircraftEngineMakeOptionsHtml({
      selectedValue: selectedMake,
      escapeHtml,
    });
  }

  function populateAircraftEngineModelSelect(form = null, make = "", selectedModel = "") {
    form = aircraftEditorForm(form);
    const modelField = aircraftEditorField(form, "engineModel");
    if (!modelField) return;
    modelField.innerHTML = aircraftEngineModelOptionsHtml({
      make,
      selectedValue: selectedModel,
      escapeHtml,
    });
  }

  function setAircraftEngineSelection(make, model, form = null) {
    form = aircraftEditorForm(form);
    if (!form) return;
    const normalizedMake = String(make ?? "").trim();
    const normalizedModel = String(model ?? "").trim();
    const canonicalMake = canonicalAircraftEngineMake(normalizedMake);
    const selectedMake = canonicalMake || (normalizedMake ? AIRCRAFT_ENGINE_OTHER_CHOICE : "");
    populateAircraftEngineMakeSelect(form, selectedMake);
    const makeField = aircraftEditorField(form, "engineMake");
    if (makeField) makeField.value = selectedMake;
    const makeOtherField = aircraftEditorField(form, "engineMakeOther");
    if (makeOtherField) makeOtherField.value = selectedMake === AIRCRAFT_ENGINE_OTHER_CHOICE ? normalizedMake : "";

    const catalogEntry = selectedMake ? aircraftEngineCatalogEntryForMakeModel(selectedMake, normalizedModel) : null;
    const selectedModel = catalogEntry ? catalogEntry.model : (normalizedModel ? AIRCRAFT_ENGINE_OTHER_CHOICE : "");
    populateAircraftEngineModelSelect(form, selectedMake, selectedModel);
    const modelField = aircraftEditorField(form, "engineModel");
    if (modelField) modelField.value = selectedModel;
    const modelOtherField = aircraftEditorField(form, "engineModelOther");
    if (modelOtherField) modelOtherField.value = selectedModel === AIRCRAFT_ENGINE_OTHER_CHOICE ? normalizedModel : "";
    syncAircraftEngineOtherFields(form);
  }

  function aircraftEditorNameForFieldId(fieldId) {
    return {
      aircraftTailInput: "tailNumber",
      aircraftMakeInput: "make",
      aircraftModelInput: "model",
      aircraftTypeInput: "type",
      aircraftEngineProfileInput: "engineModel",
      aircraftEngineMakeInput: "engineMake",
      aircraftEngineModelInput: "engineModel",
      propellerType: "propellerType",
      aircraftFuelInput: "fuelChoice",
      aircraftMonitorMakeProfileInput: "monitorMake",
      aircraftMonitorModelProfileInput: "monitorModel",
      aircraftEngineCountInput: "engineCount",
      aircraftPropellerFields: "propeller",
    }[fieldId] ?? "make";
  }

  function setAircraftFuelFields(fuel = "", form = null) {
    const AIRCRAFT_FUEL_CHOICES = getAircraftFuelChoices();
    const AIRCRAFT_FUEL_OTHER_CHOICE = getAircraftFuelOtherChoice();
    form = aircraftEditorForm(form);
    const fuelChoiceField = aircraftEditorField(form, "fuelChoice");
    const fuelOtherField = aircraftEditorField(form, "fuelOther");
    const fuelOtherWrapper = form?.querySelector(".aircraft-fuel-other-field");
    const value = String(fuel ?? "").trim();
    if (!fuelChoiceField) {
      const legacyFuelField = aircraftEditorField(form, "fuel");
      if (legacyFuelField) legacyFuelField.value = value;
      return;
    }
    const selectedChoice = AIRCRAFT_FUEL_CHOICES.includes(value) || value === AIRCRAFT_FUEL_OTHER_CHOICE
      ? value
      : (value ? AIRCRAFT_FUEL_OTHER_CHOICE : "");
    fuelChoiceField.value = selectedChoice;
    if (fuelOtherField) fuelOtherField.value = selectedChoice === AIRCRAFT_FUEL_OTHER_CHOICE && value !== AIRCRAFT_FUEL_OTHER_CHOICE ? value : "";
    if (fuelOtherWrapper) fuelOtherWrapper.hidden = selectedChoice !== AIRCRAFT_FUEL_OTHER_CHOICE;
  }

  function aircraftFuelFromForm(form = null) {
    const AIRCRAFT_FUEL_OTHER_CHOICE = getAircraftFuelOtherChoice();
    form = aircraftEditorForm(form);
    const fuelChoice = String(aircraftEditorField(form, "fuelChoice")?.value ?? "").trim();
    if (fuelChoice === AIRCRAFT_FUEL_OTHER_CHOICE) {
      return String(aircraftEditorField(form, "fuelOther")?.value ?? "").trim();
    }
    return fuelChoice || String(aircraftEditorField(form, "fuel")?.value ?? "").trim();
  }

  function syncAircraftTurboLimitFields(form = null, checked = null) {
    form = aircraftEditorForm(form);
    if (!form) return;
    const isTurboInput = aircraftEditorField(form, "isTurbo");
    const turboChecked = checked ?? Boolean(isTurboInput?.checked);
    form.querySelector(".aircraft-turbo-limits")?.toggleAttribute("hidden", !turboChecked);
    ["maxTitF", "maxMapInHg"].forEach((name) => {
      const field = aircraftEditorField(form, name);
      if (!field) return;
      field.required = turboChecked;
      field.toggleAttribute?.("required", turboChecked);
      if (turboChecked) field.setAttribute?.("aria-required", "true");
      else field.removeAttribute?.("aria-required");
    });
  }

  // Keep generic screening defaults separate from saved aircraft limits.
  function updateAircraftOilLimitPlaceholders(form = null) {
    form = aircraftEditorForm(form);
    if (!form) return;
    const make = String(aircraftEditorField(form, "engineMake")?.value ?? "").trim();
    const defaults = oilLimitDefaultsForMake(make);
    const oilTempInput = aircraftEditorField(form, "oilTempMax");
    if (oilTempInput) oilTempInput.placeholder = "Not set";
    const oilPressureInput = aircraftEditorField(form, "oilPressureMax");
    if (oilPressureInput) oilPressureInput.placeholder = "Not set";
    const defaultHelp = form.querySelector("#oilDefaultsHelp");
    if (defaultHelp) defaultHelp.textContent = `Unset maximums use generic screening values: ${defaults.oilTempMaxF} °F and ${defaults.oilPressureMaxPsi} psi. These are not saved aircraft limits.`;
  }

  function resetAircraftForm(profile = null, form = null) {
    const ENGINE_LABEL_MODES = getEngineLabelModes();
    form = aircraftEditorForm(form);
    if (!form) return;
    const values = aircraftProfileEditorValues(profile);
    form.dataset.aircraftDatabaseId = values.databaseId || "";
    form.dataset.aircraftOriginalTail = values.tailNumber || "";
    form.reset();
    form.querySelector(".aircraft-optional-details")?.removeAttribute("open");
    aircraftEditorField(form, "tailNumber").value = values.tailNumber;
    aircraftEditorField(form, "make").value = values.make;
    aircraftEditorField(form, "model").value = values.model;
    aircraftEditorField(form, "type").value = values.type;
    setAircraftEngineSelection(values.engineMake, values.engineModel, form);
    aircraftEditorField(form, "propellerType").value = values.propellerType;
    setAircraftFuelFields(values.fuel, form);
    const monitor = monitorSelectionFromText(values.monitor);
    populateMonitorSelects(
      aircraftEditorField(form, "monitorMake"),
      aircraftEditorField(form, "monitorModel"),
      monitor.make,
      monitor.model,
    );
    aircraftEditorField(form, "engineCount").value = values.engineCount;
    let twin = form.querySelector("[data-cgr-twin-config]");
    if (!twin) {
      twin = document.createElement("div");
      twin.dataset.cgrTwinConfig = "";
      twin.className = "aircraft-form-span-2";
      twin.innerHTML = `
        <label class="cgr-separate-toggle"><input type="checkbox" name="cgrSeparateEngines"> Separate engine monitors for each engine</label>
        <div data-cgr-monitor-fields>
          <p class="upload-dialog-copy">Each engine records its own log file. Upload both logs to combine them into one flight.</p>
          <div class="aircraft-form-grid">
            <label class="upload-field">
              <span data-cgr-left-label>Left engine monitor</span>
              <select aria-label="First engine monitor"><option>CGR-30P</option></select>
            </label>
            <label class="upload-field">
              <span data-cgr-right-label>Right engine monitor</span>
              <select aria-label="Second engine monitor"><option>CGR-30P</option></select>
            </label>
            <label class="upload-field">
              <span>Shared supplemental monitor</span>
              <select aria-label="Shared supplemental monitor"><option>CGR-30C (optional)</option></select>
            </label>
          </div>
          <p class="upload-dialog-copy" data-cgr-unit-summary></p>
          <button type="button" class="secondary-action" data-cgr-assignments>Monitor assignments</button>
        </div>
      `;
      aircraftEditorField(form, "monitorModel").closest("label").parentElement.append(twin);
      form.addEventListener("change", () => twin.updateCgrSettings?.());
      twin.querySelector("input").addEventListener("change", () => {
        if (twin.querySelector("input").checked) {
          populateMonitorSelects(
            aircraftEditorField(form, "monitorMake"),
            aircraftEditorField(form, "monitorModel"),
            "Electronics International",
            "CGR-30P + 30C",
          );
        }
      });
    }
    twin.querySelector("input").checked = profile?.monitor?.separateEngines === true;
    twin.updateCgrSettings = () => {
      twin.hidden = Number(aircraftEditorField(form, "engineCount").value) !== 2;
      twin.querySelector("[data-cgr-monitor-fields]").hidden = !twin.querySelector("input").checked;
      const frontRear = aircraftEditorField(form, "engineLabelMode")?.value === "front-rear";
      const labels = {
        left: frontRear ? "Front" : "Left",
        right: frontRear ? "Rear" : "Right",
        shared: "Shared",
      };
      twin.querySelector("[data-cgr-left-label]").textContent = `${labels.left} engine monitor`;
      twin.querySelector("[data-cgr-right-label]").textContent = `${labels.right} engine monitor`;
      const units = Object.entries(profile?.monitor?.cgrTwin?.units ?? {});
      twin.querySelector("[data-cgr-unit-summary]").textContent = units.length
        ? units.map(([unit, role]) => `${labels[role] ?? role}: unit ${unit}`).join(". ")
        : "You will assign each monitor to an engine when you upload its first log.";
    };
    const assignmentsButton = twin.querySelector("[data-cgr-assignments]");
    assignmentsButton.disabled = !form.dataset.aircraftDatabaseId
      || !Object.keys(profile?.monitor?.cgrTwin?.units ?? {}).length;
    assignmentsButton.onclick = () => openCgrMonitorAssignments(form.dataset.aircraftDatabaseId);
    twin.updateCgrSettings();
    const labelModeField = aircraftEditorField(form, "engineLabelMode");
    if (labelModeField) labelModeField.value = values.engineLabelMode || ENGINE_LABEL_MODES.LEFT_RIGHT;
    twin.updateCgrSettings();
    renderAircraftEngineLayoutFields(values.cylindersPerEngine, values.type, values.engineCount, form);
    renderAircraftPropellerFields(values.propellers, values.type, values.engineCount, form);
    renderAircraftFuelPressureFields(profile?.operatingLimits?.fuelPressure ?? {}, values.type, values.engineCount, form);
    const editorEngines = Array.isArray(profile?.engine?.engines) ? profile.engine.engines : [];
    renderAircraftEnginePerformanceFields(
      editorEngines.map((engine) => (engine && typeof engine === "object" ? engine.ratedHorsepower : null)),
      editorEngines.map((engine) => (engine && typeof engine === "object" ? engine.takeoffFuelFlowGph : null)),
      values.type,
      values.engineCount,
      form,
    );
    const isTurboInput = aircraftEditorField(form, "isTurbo");
    if (isTurboInput) isTurboInput.checked = values.isTurbo === true;
    const isFuelInjectedInput = aircraftEditorField(form, "isFuelInjected");
    if (isFuelInjectedInput) isFuelInjectedInput.checked = engineFuelSystemIsInjected(profile?.engine?.fuelSystem ?? values.fuelSystem);
    const maxTitInput = aircraftEditorField(form, "maxTitF");
    if (maxTitInput) maxTitInput.value = values.maxTitF;
    const maxMapInput = aircraftEditorField(form, "maxMapInHg");
    if (maxMapInput) maxMapInput.value = values.maxMapInHg;
    const oilTempMaxInput = aircraftEditorField(form, "oilTempMax");
    if (oilTempMaxInput) oilTempMaxInput.value = values.oilTempMax;
    const oilPressureMaxInput = aircraftEditorField(form, "oilPressureMax");
    if (oilPressureMaxInput) oilPressureMaxInput.value = values.oilPressureMax;
    form.querySelectorAll("[data-oil-pressure-setting]").forEach((input) => {
      input.value = values.oilPressureSettings?.[input.dataset.oilPressureSetting] ?? "";
    });
    updateAircraftOilLimitPlaceholders(form);
    syncAircraftTurboLimitFields(form);
    const photoInput = form.querySelector("#aircraftPhotoInput");
    if (photoInput) photoInput.value = "";
    const archiveButton = form.querySelector("#aircraftEditDialogDelete");
    if (archiveButton) {
      archiveButton.hidden = !values.tailNumber;
      archiveButton.disabled = !values.tailNumber || appReadOnlyMode();
      archiveButton.dataset.aircraftArchive = values.tailNumber || "";
      archiveButton.title = appReadOnlyMode()
        ? readOnlyActionMessage("Archiving aircraft")
        : values.tailNumber
        ? `Archive ${aircraftDisplayLabel(values.tailNumber)} (moves it to the Archive tab in My Account)`
        : "";
    }
    setAircraftFormPhotoDataUrl(values.photoDataUrl, values.tailNumber);
    setAircraftFormStatus(appReadOnlyMode() ? readOnlyPreviewMessage() : "", "info", form);
    updateAircraftEditSummary(form);
    syncReadOnlyUi();
  }

  function formEngineCount(form = null) {
    const value = Number.parseInt(String(aircraftEditorField(form, "engineCount")?.value ?? "").trim(), 10);
    return value === 2 ? 2 : 1;
  }

  function aircraftEngineLabelMode(profile = null, type = null, count = null) {
    const ENGINE_LABEL_MODES = getEngineLabelModes();
    const raw = String(profile?.engineLayout?.labelMode ?? profile?.engineLayout?.engineLabelMode ?? "").trim().toLowerCase();
    const normalizedType = normalizedAircraftType(type ?? profile?.aircraft?.type, profile) ?? "";
    const engineCount = Number.isFinite(Number(count)) ? Number(count) : Number(profile?.engineLayout?.count ?? profile?.engine?.count);
    if (raw === ENGINE_LABEL_MODES.FRONT_REAR || raw === "front/rear") return ENGINE_LABEL_MODES.FRONT_REAR;
    if (raw === ENGINE_LABEL_MODES.LEFT_RIGHT || raw === "left/right") return ENGINE_LABEL_MODES.LEFT_RIGHT;
    if (normalizedType === "multi_centerline") return ENGINE_LABEL_MODES.FRONT_REAR;
    return engineCount > 1 ? ENGINE_LABEL_MODES.LEFT_RIGHT : "";
  }

  function aircraftCylindersPerEngine(profile = null, count = 1) {
    const layoutCounts = Array.isArray(profile?.engineLayout?.cylindersPerEngine) ? profile.engineLayout.cylindersPerEngine : [];
    const engines = Array.isArray(profile?.engine?.engines) ? profile.engine.engines : [];
    const singleEngineCount = Number(profile?.engine?.cylinders);
    return Array.from({ length: Math.max(1, Math.min(12, Number(count) || 1)) }, (_, index) => {
      const value = Number(layoutCounts[index] ?? engines[index]?.cylinders ?? (index === 0 ? singleEngineCount : null));
      return Number.isInteger(value) && value > 0 ? String(value) : "";
    });
  }

  function aircraftPropellerEntriesFromForm(form = null) {
    form = aircraftEditorForm(form);
    return Array.from(form?.querySelectorAll("[data-aircraft-propeller-input]") ?? []).map((input, index) => {
      const make = String(form?.querySelector(`[data-aircraft-propeller-make-input="${index}"]`)?.value ?? "").trim();
      return {
        engineId: `engine${index + 1}`,
        ...(make ? { make } : {}),
        value: input.value,
      };
    });
  }

  function aircraftCylinderEntriesFromForm(form = null) {
    form = aircraftEditorForm(form);
    return Array.from(form?.querySelectorAll("[data-aircraft-cylinder-input]") ?? []).map((input) => input.value);
  }

  function cylinderGroupsForLayout(cylindersPerEngine = []) {
    let nextSource = 1;
    return cylindersPerEngine.map((count) => {
      if (!Number.isInteger(count) || count <= 0) return [];
      const group = Array.from({ length: count }, (_, offset) => nextSource + offset);
      nextSource += count;
      return group;
    });
  }

  function syncAircraftLayoutControls(form = null, existingPropellers = null, existingCylinders = null) {
    form = aircraftEditorForm(form);
    if (!form) return;
    const propellers = existingPropellers ?? aircraftPropellerEntriesFromForm(form);
    const cylinders = existingCylinders ?? aircraftCylinderEntriesFromForm(form);
    renderAircraftEngineLayoutFields(cylinders, null, null, form);
    renderAircraftPropellerFields(propellers, null, null, form);
    const performance = aircraftEnginePerformanceEntriesFromForm(form);
    renderAircraftEnginePerformanceFields(
      performance.map((entry) => entry.ratedHorsepower),
      performance.map((entry) => entry.takeoffFuelFlowGph),
      null,
      null,
      form,
    );
    renderAircraftFuelPressureFields(null, null, null, form);
    updateAircraftEditSummary(form);
  }

  function savedFuelPressureSettings(form) {
    const profiles = getAircraftProfiles();
    const originalTail = form?.dataset?.aircraftOriginalTail;
    const tail = String(aircraftEditorField(form, "tailNumber")?.value ?? "").trim().toUpperCase();
    return (profiles?.[originalTail] ?? profiles?.[tail])?.operatingLimits?.fuelPressure ?? {};
  }

  function fuelPressureSettingsWithEngines(raw, count) {
    const settings = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
    const byEngine = settings.byEngine && typeof settings.byEngine === "object" && !Array.isArray(settings.byEngine)
      ? { ...settings.byEngine }
      : count === 1 && !Object.hasOwn(settings, "byEngine") && Object.keys(settings).length
        ? { engine1: { ...settings } } : {};
    return { ...settings, byEngine };
  }

  function aircraftFuelPressureSettingsFromForm(form = null) {
    form = aircraftEditorForm(form);
    if (!form) return { byEngine: {} };
    const settings = fuelPressureSettingsWithEngines(
      fuelPressureDrafts.get(form) ?? savedFuelPressureSettings(form),
      formEngineCount(form),
    );
    form.querySelectorAll("[data-fuel-pressure-setting]").forEach((input) => {
      const engineId = input.dataset.fuelPressureEngine;
      const name = input.dataset.fuelPressureSetting;
      const current = settings.byEngine[engineId];
      const draft = current && typeof current === "object" && !Array.isArray(current) ? { ...current } : {};
      const text = String(input.value ?? "").trim();
      draft[name] = name === "minPsi" || name === "maxPsi" ? text && Number.isFinite(Number(text)) ? Number(text) : text || null
          : text || null;
      settings.byEngine[engineId] = draft;
    });
    form.querySelectorAll('[data-fuel-pressure-setting="minPsi"]').forEach((input) => {
      const draft = settings.byEngine[input.dataset.fuelPressureEngine];
      // Keep the existing analysis enable flag; entering valid limits now enables it.
      draft.verified = normalizeFuelPressureConfiguration({ ...draft, verified: true }).usable;
    });
    fuelPressureDrafts.set(form, settings);
    return settings;
  }

  function renderAircraftFuelPressureFields(existingSettings = null, typeValue = null, countValue = null, form = null) {
    form = aircraftEditorForm(form);
    const container = form?.querySelector(".aircraft-fuel-pressure-settings");
    if (!container) return;
    const count = Number(countValue) === 2 ? 2 : countValue === null ? formEngineCount(form) : 1;
    const type = normalizedAircraftType(typeValue ?? aircraftEditorField(form, "type")?.value) ?? "";
    const settings = existingSettings === null ? aircraftFuelPressureSettingsFromForm(form)
      : fuelPressureSettingsWithEngines(existingSettings, count);
    fuelPressureDrafts.set(form, settings);
    const labelMode = String(aircraftEditorField(form, "engineLabelMode")?.value ?? "");
    const frontRear = type === "multi_centerline" || labelMode === getEngineLabelModes().FRONT_REAR;
    container.innerHTML = Array.from({ length: count }, (_, index) => {
      const engineId = `engine${index + 1}`;
      const raw = settings.byEngine[engineId];
      const config = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
      const label = count === 1 ? "Engine" : frontRear ? (index === 0 ? "Front engine" : "Rear engine") : index === 0 ? "Left engine" : "Right engine";
      const field = (name, title, extra = "") => {
        const value = config[name] ?? "";
        return `<label class="upload-field"><span>${title}</span><input name="fuelPressure${index + 1}${name}" data-fuel-pressure-engine="${engineId}" data-fuel-pressure-setting="${name}" type="text" ${extra} autocomplete="off" value="${escapeHtml(value)}" /></label>`;
      };
      return `<section class="aircraft-fuel-pressure-engine" aria-label="${label} fuel pressure"><h4>${label} fuel pressure</h4>
        <div class="aircraft-form-grid">
          ${field("minPsi", "Minimum (psi)", 'inputmode="decimal" placeholder="Not set"')}
          ${field("maxPsi", "Maximum (psi)", 'inputmode="decimal" placeholder="Not set"')}
        </div>
      </section>`;
    }).join("");
  }

  function aircraftFuelPressureFormError(form) {
    const settings = aircraftFuelPressureSettingsFromForm(form);
    const messages = {
      invalid_min_psi: ["minPsi", "Enter a finite minimum fuel pressure in psi."],
      invalid_max_psi: ["maxPsi", "Enter a finite maximum fuel pressure in psi."],
      invalid_range: ["minPsi", "Minimum fuel pressure must be below the maximum."],
      negative_absolute_pressure: ["minPsi", "Absolute pressure limits cannot be negative."],
      missing_bounds: ["minPsi", "Verified fuel-pressure settings need at least one pressure limit."],
    };
    for (let index = 0; index < formEngineCount(form); index += 1) {
      const engineId = `engine${index + 1}`;
      const fieldFor = (name) => form?.querySelector(`[data-fuel-pressure-engine="${engineId}"][data-fuel-pressure-setting="${name}"]`);
      if (!fieldFor("minPsi")) continue;
      const config = normalizeFuelPressureConfiguration(settings.byEngine[engineId]);
      const code = config.errors.find((error) => config.verified || !error.startsWith("missing_"));
      if (code) {
        const [name, message] = messages[code];
        return { field: fieldFor(name), message: `${formEngineCount(form) > 1 ? `Engine ${index + 1}: ` : ""}${message}` };
      }
    }
    return null;
  }

  function applyAircraftEngineMakeSelection(form = null) {
    form = aircraftEditorForm(form);
    const make = String(aircraftEditorField(form, "engineMake")?.value ?? "").trim();
    populateAircraftEngineModelSelect(form, make, "");
    updateAircraftOilLimitPlaceholders(form);
    const modelOtherField = aircraftEditorField(form, "engineModelOther");
    if (modelOtherField && make !== AIRCRAFT_ENGINE_OTHER_CHOICE) modelOtherField.value = "";
    syncAircraftEngineOtherFields(form);
    updateAircraftEditSummary(form);
  }

  function aircraftCatalogNumberFieldValue(value, digits = 1) {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return "";
    return number.toFixed(digits).replace(/\.0$/, "");
  }

  function applyAircraftEngineModelSelection(form = null) {
    form = aircraftEditorForm(form);
    const make = String(aircraftEditorField(form, "engineMake")?.value ?? "").trim();
    const model = String(aircraftEditorField(form, "engineModel")?.value ?? "").trim();
    syncAircraftEngineOtherFields(form);
    const entry = aircraftEngineCatalogEntryForMakeModel(make, model);
    if (!form || !entry) {
      updateAircraftEditSummary(form);
      return false;
    }

    const engineCount = formEngineCount(form);
    const existingPropellers = aircraftPropellerEntriesFromForm(form);
    const cylinderValue = Number.isInteger(Number(entry.cylinders)) && Number(entry.cylinders) > 0 ? String(Number(entry.cylinders)) : "";
    if (cylinderValue) {
      syncAircraftLayoutControls(
        form,
        existingPropellers,
        Array.from({ length: engineCount }, () => cylinderValue),
      );
    }

    const hp = aircraftCatalogNumberFieldValue(entry.ratedHorsepower, 0);
    if (hp) {
      Array.from(form.querySelectorAll("[data-aircraft-hp-input]") ?? []).forEach((input) => {
        input.value = hp;
      });
    }
    const fuelFlow = aircraftCatalogNumberFieldValue(entry.takeoffFuelFlowGph, 1);
    if (fuelFlow) {
      Array.from(form.querySelectorAll("[data-aircraft-ff-input]") ?? []).forEach((input) => {
        input.value = fuelFlow;
      });
    }

    const isFuelInjectedInput = aircraftEditorField(form, "isFuelInjected");
    if (isFuelInjectedInput) isFuelInjectedInput.checked = engineFuelSystemIsInjected(entry.fuelSystem ?? entry.model);
    const isTurboInput = aircraftEditorField(form, "isTurbo");
    if (isTurboInput) isTurboInput.checked = /turbo/i.test(String(entry.aspiration ?? entry.model ?? ""));
    syncAircraftTurboLimitFields(form);
    updateAircraftEditSummary(form);
    return true;
  }

  function formTextFieldIsBlank(form, name) {
    return !String(aircraftEditorField(form, name)?.value ?? "").trim();
  }

  function setTextFieldIfBlank(form, name, value) {
    const field = aircraftEditorField(form, name);
    const text = String(value ?? "").trim();
    if (!field || !text || String(field.value ?? "").trim()) return false;
    field.value = text;
    return true;
  }

  function applyAircraftRegistryPrefill(registryAircraft = null, form = null) {
    form = aircraftEditorForm(form);
    if (!form || !registryAircraft) return [];
    const applied = [];
    if (setTextFieldIfBlank(form, "make", registryAircraft.make)) applied.push("make");
    if (setTextFieldIfBlank(form, "model", registryAircraft.model)) applied.push("model");

    const aircraftType = String(registryAircraft.aircraftType ?? "").trim();
    const typeField = aircraftEditorField(form, "type");
    if (typeField && aircraftType && !String(typeField.value ?? "").trim()) {
      typeField.value = aircraftType;
      applied.push("type");
    }

    const engineCount = Number(registryAircraft.engineCount);
    const engineCountField = aircraftEditorField(form, "engineCount");
    if (engineCountField && engineCount === 2 && String(engineCountField.value ?? "1") !== "2") {
      engineCountField.value = "2";
      applied.push("engineCount");
    }

    const engineMakeBlank = formTextFieldIsBlank(form, "engineMake") && formTextFieldIsBlank(form, "engineMakeOther");
    if (engineMakeBlank && registryAircraft.engineMake) {
      setAircraftEngineSelection(registryAircraft.engineMake, "", form);
      applied.push("engineMake");
    } else {
      syncAircraftEngineOtherFields(form);
    }

    if (applied.includes("type") || applied.includes("engineCount")) {
      syncAircraftLayoutControls(form);
    }
    updateAircraftEditSummary(form);
    return applied;
  }

  function performanceFieldValue(raw, max) {
    const value = Number.parseFloat(String(raw ?? "").trim());
    if (!Number.isFinite(value) || value <= 0) return null;
    return Math.min(max, value);
  }

  function enginePerformanceQualifier(index, count, type) {
    if (count <= 1) return "";
    if (type === "multi_centerline") return index === 0 ? "Front" : index === 1 ? "Rear" : `Engine ${index + 1}`;
    return index === 0 ? "Left" : index === 1 ? "Right" : `Engine ${index + 1}`;
  }

  // Per-engine optional targets for the takeoff fuel-flow enrichment detector.
  // Read back from the live form for re-render preservation (querySelectorAll,
  // like the propeller entries); aircraftProfileFromForm reads them by index.
  function aircraftEnginePerformanceEntriesFromForm(form = null) {
    form = aircraftEditorForm(form);
    return Array.from(form?.querySelectorAll("[data-aircraft-hp-input]") ?? []).map((input, index) => ({
      engineId: `engine${index + 1}`,
      ratedHorsepower: performanceFieldValue(input.value, 2000),
      takeoffFuelFlowGph: performanceFieldValue(form?.querySelector(`[data-aircraft-ff-input="${index}"]`)?.value, 200),
    }));
  }

  function renderAircraftEnginePerformanceFields(hpValues = [], ffValues = [], typeValue = null, countValue = null, form = null) {
    form = aircraftEditorForm(form);
    const container = form?.querySelector(".aircraft-engine-performance-grid");
    if (!container) return;
    const parsedCount = Number.parseInt(String(countValue ?? ""), 10);
    const count = Number.isFinite(parsedCount) && parsedCount > 0 ? Math.max(1, Math.min(12, parsedCount)) : formEngineCount(form);
    const type = normalizedAircraftType(typeValue ?? aircraftEditorField(form, "type")?.value) ?? "";
    const hps = Array.isArray(hpValues) ? hpValues : [];
    const ffs = Array.isArray(ffValues) ? ffValues : [];
    container.innerHTML = Array.from({ length: count }, (_, index) => {
      const qualifier = enginePerformanceQualifier(index, count, type);
      const hp = Number.isFinite(Number(hps[index])) && Number(hps[index]) > 0 ? String(hps[index]) : "";
      const ff = Number.isFinite(Number(ffs[index])) && Number(ffs[index]) > 0 ? String(ffs[index]) : "";
      const hpLabel = qualifier ? `Rated takeoff HP (${qualifier})` : "Rated takeoff HP";
      const ffLabel = qualifier ? `Full-power fuel flow gph (${qualifier})` : "Full-power fuel flow (gph)";
      return `
        <label class="upload-field">
          <span>${escapeHtml(hpLabel)}</span>
          <input data-aircraft-hp-input="${index}" name="engineRatedHp${index + 1}" type="number" inputmode="numeric" min="0" max="2000" step="1" placeholder="Not set" autocomplete="off" value="${escapeHtml(hp)}" />
        </label>
        <label class="upload-field">
          <span>${escapeHtml(ffLabel)}</span>
          <input data-aircraft-ff-input="${index}" name="engineTakeoffFf${index + 1}" type="number" inputmode="decimal" min="0" max="200" step="0.1" placeholder="Not set" autocomplete="off" value="${escapeHtml(ff)}" />
        </label>
      `;
    }).join("");
  }

  function renderAircraftEngineLayoutFields(existingCounts = [], typeValue = null, countValue = null, form = null) {
    const ENGINE_LABEL_MODES = getEngineLabelModes();
    form = aircraftEditorForm(form);
    const container = form?.querySelector(".aircraft-engine-layout-grid");
    const labelModeField = form?.querySelector(".aircraft-engine-label-mode-field");
    const labelModeSelect = aircraftEditorField(form, "engineLabelMode");
    if (!container) return;
    const parsedCount = Number.parseInt(String(countValue ?? ""), 10);
    const count = Number.isFinite(parsedCount) && parsedCount > 0 ? Math.max(1, Math.min(12, parsedCount)) : formEngineCount(form);
    const type = normalizedAircraftType(typeValue ?? aircraftEditorField(form, "type")?.value) ?? "";
    const currentMode = String(labelModeSelect?.value ?? "").trim();
    const mode = count > 1
      ? (type === "multi_centerline" ? ENGINE_LABEL_MODES.FRONT_REAR : (currentMode || ENGINE_LABEL_MODES.LEFT_RIGHT))
      : "";
    if (labelModeSelect) labelModeSelect.value = mode || ENGINE_LABEL_MODES.LEFT_RIGHT;
    if (labelModeField) labelModeField.hidden = count <= 1;
    const labels = mode === ENGINE_LABEL_MODES.FRONT_REAR
      ? ["Cylinders (Front)", "Cylinders (Rear)"]
      : ["Cylinders (Left)", "Cylinders (Right)"];
    container.innerHTML = Array.from({ length: count }, (_, index) => {
      const value = String(existingCounts[index] ?? "").trim();
      const label = count <= 1 ? "Engine cylinders" : (labels[index] ?? `Cylinders ${index + 1}`);
      return `
      <label class="upload-field">
        <span>${escapeHtml(label)}</span>
        <input data-aircraft-cylinder-input="${index}" name="engineCylinders${index + 1}" type="text" inputmode="numeric" maxlength="2" placeholder="${count <= 1 ? "4" : "6"}" autocomplete="off" value="${escapeHtml(value)}" />
      </label>
    `;
    }).join("");
  }

  function renderAircraftPropellerFields(existingEntries = [], typeValue = null, countValue = null, form = null) {
    form = aircraftEditorForm(form);
    const container = form?.querySelector(".aircraft-propeller-grid");
    if (!container) return;
    const parsedCount = Number.parseInt(String(countValue ?? ""), 10);
    const count = Number.isFinite(parsedCount) && parsedCount > 0 ? Math.max(1, Math.min(12, parsedCount)) : formEngineCount(form);
    const type = normalizedAircraftType(typeValue ?? aircraftEditorField(form, "type")?.value) ?? "";
    const entries = Array.isArray(existingEntries) ? existingEntries : [];
    container.innerHTML = Array.from({ length: count }, (_, index) => {
      const existing = entries[index] ?? {};
      const label = (existing && typeof existing === "object" ? existing.label : "") || propellerEngineLabel(index, count, type);
      const makeLabel = count <= 1 ? "Propeller manufacturer" : `${label} manufacturer`;
      const modelLabel = count <= 1 ? "Propeller model" : `${label} model`;
      const make = propellerEntryMake(existing);
      const model = propellerEntryModel(existing);
      return `
      <label class="upload-field">
        <span>${escapeHtml(makeLabel)}</span>
        <select data-aircraft-propeller-make-input="${index}" name="propellerMake${index + 1}">
          ${propellerMakeOptionsHtml(make)}
        </select>
      </label>
      <label class="upload-field">
        <span>${escapeHtml(modelLabel)}</span>
        <input data-aircraft-propeller-input="${index}" name="propeller${index + 1}" type="text" maxlength="160" placeholder="HC-C2YK-2BF" autocomplete="off" value="${escapeHtml(model)}" />
      </label>
    `;
    }).join("");
  }

  function aircraftProfileFromForm(form = null) {
    const ENGINE_LABEL_MODES = getEngineLabelModes();
    const AIRCRAFT_PROPELLER_TYPES = getAircraftPropellerTypes();
    form = aircraftEditorForm(form);
    const tailNumber = String(aircraftEditorField(form, "tailNumber")?.value ?? "").trim().toUpperCase();
    const make = String(aircraftEditorField(form, "make")?.value ?? "").trim();
    const model = limitedAircraftModel(aircraftEditorField(form, "model")?.value);
    const type = normalizedAircraftType(aircraftEditorField(form, "type")?.value) ?? null;
    const engineMake = aircraftEngineMakeFromForm(form);
    const engineModel = aircraftEngineModelFromForm(form);
    const fuel = aircraftFuelFromForm(form);
    const propellerType = String(aircraftEditorField(form, "propellerType")?.value ?? "").trim();
    const monitor = selectedMonitorFromSelects(
      aircraftEditorField(form, "monitorMake"),
      aircraftEditorField(form, "monitorModel"),
    );
    const monitorText = supportedMonitorLabel(monitor);
    const engineCount = formEngineCount(form);
    const rawLabelMode = String(aircraftEditorField(form, "engineLabelMode")?.value ?? "").trim();
    const labelMode = engineCount > 1
      ? (rawLabelMode === ENGINE_LABEL_MODES.FRONT_REAR || type === "multi_centerline" ? ENGINE_LABEL_MODES.FRONT_REAR : ENGINE_LABEL_MODES.LEFT_RIGHT)
      : "";
    const cylindersPerEngine = Array.from({ length: engineCount }, (_, index) => {
      const value = Number.parseInt(String(form?.querySelector(`[data-aircraft-cylinder-input="${index}"]`)?.value ?? "").trim(), 10);
      return Number.isInteger(value) && value > 0 ? Math.min(16, value) : null;
    });
    const cylinderGroups = cylindersPerEngine.every((value) => Number.isInteger(value) && value > 0)
      ? cylinderGroupsForLayout(cylindersPerEngine)
      : [];
    const formDatabaseId = String(form?.dataset?.aircraftDatabaseId ?? "").trim() || null;
    const isTurbo = Boolean(aircraftEditorField(form, "isTurbo")?.checked);
    const isFuelInjected = Boolean(aircraftEditorField(form, "isFuelInjected")?.checked);
    const maxTitFRaw = Number.parseFloat(String(aircraftEditorField(form, "maxTitF")?.value ?? "").trim());
    const maxMapInHgRaw = Number.parseFloat(String(aircraftEditorField(form, "maxMapInHg")?.value ?? "").trim());
    // Reading the field values in JS bypasses the inputs' native min/max
    // validation, so clamp to the same bounds (TIT 800-2200 F, MAP 20-80 inHg)
    // before persisting rather than letting an out-of-range entry through.
    const maxTitF = Number.isFinite(maxTitFRaw) && maxTitFRaw > 0 ? Math.min(2200, Math.max(800, maxTitFRaw)) : null;
    const maxMapInHg = Number.isFinite(maxMapInHgRaw) && maxMapInHgRaw > 0 ? Math.min(80, Math.max(20, maxMapInHgRaw)) : null;
    const oilTempMaxRaw = Number.parseFloat(String(aircraftEditorField(form, "oilTempMax")?.value ?? "").trim());
    const oilPressureMaxRaw = Number.parseFloat(String(aircraftEditorField(form, "oilPressureMax")?.value ?? "").trim());
    // Same JS-bypasses-native-validation clamp as the turbo limits above
    // (oil temp 180-320 F, oil pressure 40-150 psi); empty/invalid -> null so
    // the detector falls back to the make default.
    const oilTempMax = Number.isFinite(oilTempMaxRaw) && oilTempMaxRaw > 0 ? Math.min(320, Math.max(180, oilTempMaxRaw)) : null;
    const oilPressureMax = Number.isFinite(oilPressureMaxRaw) && oilPressureMaxRaw > 0 ? Math.min(150, Math.max(40, oilPressureMaxRaw)) : null;
    const propellers = Array.from({ length: engineCount }, (_, index) => {
      const make = String(form?.querySelector(`[data-aircraft-propeller-make-input="${index}"]`)?.value ?? "").trim();
      const value = String(form?.querySelector(`[data-aircraft-propeller-input="${index}"]`)?.value ?? "").trim();
      return {
        engineId: `engine${index + 1}`,
        label: propellerEngineLabel(index, engineCount, type ?? ""),
        ...(make ? { make } : {}),
        value,
      };
    });
    const enginePerformance = Array.from({ length: engineCount }, (_, index) => ({
      ratedHorsepower: performanceFieldValue(form?.querySelector(`[data-aircraft-hp-input="${index}"]`)?.value, 2000),
      takeoffFuelFlowGph: performanceFieldValue(form?.querySelector(`[data-aircraft-ff-input="${index}"]`)?.value, 200),
    }));
    const profiles = getAircraftProfiles();
    const originalProfile = profiles?.[form?.dataset?.aircraftOriginalTail] ?? profiles?.[tailNumber];
    const photoDataUrl = getAircraftFormPhotoDataUrl();
    const oilPressureSettings = { ...(profiles?.[tailNumber]?.operatingLimits?.oilPressure ?? {}) };
    form.querySelectorAll("[data-oil-pressure-setting]").forEach((input) => {
      const text = String(input.value ?? "").trim();
      const value = Number(text);
      oilPressureSettings[input.dataset.oilPressureSetting] = input.type === "number"
        ? text && Number.isFinite(value) && value > 0 && value >= Number(input.min) && value <= Number(input.max) ? value : null
        : text.slice(0, 300) || null;
    });
    return {
      databaseId: profiles?.[tailNumber]?.databaseId ?? formDatabaseId,
      tailNumber,
      profileStatus: "local",
      aircraft: {
        make: make || null,
        model: model || null,
        type,
      },
      engine: {
        make: engineMake || null,
        model: engineModel || null,
        propeller: propellers[0]?.value || null,
        propellerType: AIRCRAFT_PROPELLER_TYPES.includes(propellerType) ? propellerType : null,
        engines: propellers.map((entry, index) => ({
          id: entry.engineId,
          label: entry.label.replace(/\s+Propeller$/i, " Engine"),
          propeller: entry.value || null,
          cylinders: cylindersPerEngine[index] ?? null,
          ratedHorsepower: enginePerformance[index]?.ratedHorsepower ?? null,
          takeoffFuelFlowGph: enginePerformance[index]?.takeoffFuelFlowGph ?? null,
        })),
        cylinders: engineCount === 1 ? (cylindersPerEngine[0] ?? null) : null,
        aspiration: null,
        fuelSystem: isFuelInjected ? "fuel injected" : null,
      },
      propellers,
      propellerType: AIRCRAFT_PROPELLER_TYPES.includes(propellerType) ? propellerType : null,
      engineLayout: {
        count: engineCount,
        ...(labelMode ? { labelMode } : {}),
        engineIds: Array.from({ length: engineCount }, (_, index) => `engine${index + 1}`),
        cylindersPerEngine,
        egtGroups: cylinderGroups,
        chtGroups: cylinderGroups,
      },
      fuel: {
        primary: fuel || null,
        approved: fuel ? [fuel] : [],
      },
      monitor: {
        separateEngines: engineCount === 2 && Boolean(form.querySelector("[name=cgrSeparateEngines]")?.checked),
        cgrTwin: {
          ...(originalProfile?.monitor?.cgrTwin ?? {}),
          enabled: engineCount === 2 && Boolean(form.querySelector("[name=cgrSeparateEngines]")?.checked),
        },
        make: monitor.make || null,
        model: monitor.model || null,
        type: monitorText || null,
        installations: monitorText ? [{ make: monitor.make || null, model: monitor.model || null, type: monitorText }] : [],
      },
      photo: photoDataUrl
        ? {
            dataUrl: photoDataUrl,
            storage: "db-inline",
          }
        : undefined,
      probeMapping: {},
      turbo: isTurbo ? { isTurbo: true, maxTitF, maxMapInHg } : { isTurbo: false },
      operatingLimits: {
        ...originalProfile?.operatingLimits,
        chtMax: null,
        oilTempMax,
        oilPressureMax,
        oilPressure: oilPressureSettings,
        fuelPressure: aircraftFuelPressureSettingsFromForm(form),
      },
      notes: "Created locally from the Aircraft workspace.",
      observedFromLogs: {
        flightCount: aircraftFlights(tailNumber).length,
        monitors: monitorText ? [monitorText] : [],
        parsers: [],
      },
    };
  }

  function validateAircraftEditForm(form = null) {
    const AIRCRAFT_PROPELLER_TYPES = getAircraftPropellerTypes();
    form = aircraftEditorForm(form);
    const tailNumberField = aircraftEditorField(form, "tailNumber");
    const propellerTypeField = aircraftEditorField(form, "propellerType");
    const isTurbo = Boolean(aircraftEditorField(form, "isTurbo")?.checked);
    const maxTitField = aircraftEditorField(form, "maxTitF");
    const maxMapField = aircraftEditorField(form, "maxMapInHg");
    const tailNumber = String(tailNumberField?.value ?? "").trim();
    const propellerType = String(propellerTypeField?.value ?? "").trim();
    const oilField = (name) => aircraftEditorField(form, name);
    const oilValue = (name) => Number(oilField(name)?.value) || null;
    const oilMax = oilValue("oilPressureMax");
    const operatingMin = oilValue("oilOperatingMinPsi");
    const idleMin = oilValue("oilIdleMinPsi");
    const coldFields = ["oilColdMaxPsi", "oilColdMaxTempF", "oilColdMaxRpm"];
    const coldCount = coldFields.filter((name) => oilValue(name)).length;
    const oilError = oilMax && operatingMin && operatingMin >= oilMax
      ? { field: oilField("oilOperatingMinPsi"), message: "Operating minimum oil pressure must be below the maximum." }
      : idleMin && ((oilMax && idleMin >= oilMax) || (operatingMin && idleMin > operatingMin))
        ? { field: oilField("oilIdleMinPsi"), message: "Idle minimum must not exceed the operating minimum or maximum oil pressure." }
        : coldCount > 0 && (coldCount < 3 || !oilMax)
          ? { field: oilField("oilColdMaxPsi"), message: "A cold-start allowance needs all three cold settings and an operating maximum." }
          : coldCount === 3 && oilValue("oilColdMaxPsi") < oilMax
            ? { field: oilField("oilColdMaxPsi"), message: "Cold-start maximum must be at least the operating maximum oil pressure." }
            : null;
    const invalidField = !tailNumber
      ? { field: tailNumberField, message: "Tail number is required." }
      : !AIRCRAFT_PROPELLER_TYPES.includes(propellerType)
        ? { field: propellerTypeField, message: "Choose Fixed Pitch or Constant Speed before saving." }
        : isTurbo && !String(maxTitField?.value ?? "").trim()
          ? { field: maxTitField, message: "Max TIT is required when Turbocharged is checked." }
          : isTurbo && !String(maxMapField?.value ?? "").trim()
            ? { field: maxMapField, message: "Max MAP is required when Turbocharged is checked." }
        : oilError ?? aircraftFuelPressureFormError(form);
    if (!invalidField) return true;
    setAircraftFormStatus(invalidField.message, "error", form);
    let disclosure = invalidField.field?.closest?.("details");
    while (disclosure) {
      disclosure.setAttribute("open", "");
      disclosure = disclosure.parentElement?.closest?.("details");
    }
    invalidField.field?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    invalidField.field?.focus?.();
    return false;
  }

  function engineFuelSystemIsInjected(value) {
    return /fuel[\s_-]*injected|injection|injected/i.test(String(value ?? ""));
  }

  return {
    aircraftEditorForm,
    aircraftEditorField,
    setAircraftFormStatus,
    aircraftEditSummaryText,
    updateAircraftEditSummary,
    aircraftEditorNameForFieldId,
    setAircraftFuelFields,
    aircraftFuelFromForm,
    syncAircraftTurboLimitFields,
    resetAircraftForm,
    formEngineCount,
    aircraftEngineLabelMode,
    aircraftCylindersPerEngine,
    aircraftPropellerEntriesFromForm,
    aircraftCylinderEntriesFromForm,
    cylinderGroupsForLayout,
    syncAircraftLayoutControls,
    renderAircraftEngineLayoutFields,
    renderAircraftPropellerFields,
    renderAircraftEnginePerformanceFields,
    aircraftEnginePerformanceEntriesFromForm,
    renderAircraftFuelPressureFields,
    aircraftFuelPressureSettingsFromForm,
    aircraftProfileFromForm,
    validateAircraftEditForm,
    populateAircraftEngineMakeSelect,
    populateAircraftEngineModelSelect,
    setAircraftEngineSelection,
    applyAircraftEngineMakeSelection,
    applyAircraftEngineModelSelection,
    applyAircraftRegistryPrefill,
  };
}
